'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Component } from 'react';
import PropTypes from 'prop-types';
import CustomWidgetPropTypes from 'DesignData/propTypes/customWidgets';
import { connect } from 'react-redux';
import { debounce } from 'underscore';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import I18n from 'I18n';
import UIBreadcrumbs from 'UIComponents/nav/UIBreadcrumbs';
import UILink from 'UIComponents/link/UILink';
import UIFormLabel from 'UIComponents/form/UIFormLabel';
import Toolbar from 'DesignEditorsUI/components/shared/Toolbar';
import IDECodeMirror from 'DesignEditorsUI/components/shared/IDECodeMirror';
import UIButton from 'UIComponents/button/UIButton';
import { getUserHasReadAccessOnly } from 'DesignData/selectors/auth';
import * as customWidgetBuffersSelectors from 'DesignData/selectors/customWidgetBuffers';
import * as CustomWidgetEditorOptionsActions from 'DesignData/actions/CustomWidgetEditorOptions';
import * as CustomWidgetBuffersActions from 'DesignData/actions/CustomWidgetBuffers';
import { registerStatusBar } from 'DesignData/actions/StatusBar';
import { generateTranslationsFile } from 'DesignData/helpers/TranslationHelpers';
import withDescriptionInput from 'DesignEditorsUI/components/customWidgetEditor/decorators/withDescriptionInputs';
import withPartials from 'ContentComponents/cmv2/decorators/withPartials';
import { languageI18nKey } from 'DesignComponents/utils/I18nKeys';
import { compose } from 'redux';

const i18nKey = key => `DesignEditorsUI.customWidgetEditor.sidebar.main.translations.${key}`;

const DecoratedCodeMirror = withDescriptionInput(IDECodeMirror);

function makeMapStateToProps() {
  const getMessages = customWidgetBuffersSelectors.makeGetMessages();
  const getMasterLanguage = customWidgetBuffersSelectors.makeGetMasterLanguage();
  const getIsReadOnly = customWidgetBuffersSelectors.makeGetIsReadOnly();
  return (state, props) => {
    return {
      customWidgetLabel: customWidgetBuffersSelectors.getCustomWidgetLabel(state, props),
      masterLanguage: getMasterLanguage(state, props),
      messages: getMessages(state, props),
      isReadOnly: getUserHasReadAccessOnly(state) || getIsReadOnly(state, props)
    };
  };
}

const mapDispatchToProps = {
  setLanguageEditor: CustomWidgetEditorOptionsActions.setLanguageEditor,
  updateLanguage: CustomWidgetBuffersActions.updateLanguage,
  addLanguage: CustomWidgetBuffersActions.addLanguage,
  regenerateTranslations: CustomWidgetBuffersActions.regenerateTranslations,
  // Status bar
  registerStatusBar
};

class LanguageEditor extends Component {
  constructor(...args) {
    super(...args);

    this.closeEditor = () => {
      this.props.setLanguageEditor(this.props.customWidgetId, null);
    };

    this.onChange = debounce(({
      value
    }) => {
      /* TODO: Better validation, for now just don't save if invalid json */
      let __value;

      try {
        __value = JSON.parse(value);
      } catch (e) {
        return;
      }

      this.props.updateLanguage(this.props.customWidgetId, this.props.selectedLanguageEditor, __value);
    }, 1000);

    this.regenerateTranslations = () => {
      this.props.regenerateTranslations(this.props.customWidgetId, this.props.customWidgetLabel, this.props.selectedLanguageEditor);
    };
  }

  componentDidMount() {
    const {
      selectedLanguageEditor
    } = this.props;
    this.props.registerStatusBar({
      activeLanguage: selectedLanguageEditor
    });
  }

  componentDidUpdate(prevProps) {
    const translations = this.props.messages[this.props.selectedLanguageEditor];

    if (!translations || !this.props.masterLanguage) {
      this.props.setLanguageEditor(this.props.customWidgetId, null);
    }

    if (prevProps.selectedLanguageEditor !== this.props.selectedLanguageEditor) {
      this.props.registerStatusBar({
        activeLanguage: this.props.selectedLanguageEditor
      });
    }
  }

  componentWillUnmount() {
    this.props.registerStatusBar({
      activeLanguage: null
    });
  }

  get value() {
    const {
      selectedLanguageEditor,
      messages,
      masterLanguage,
      addLanguage,
      customWidgetId,
      customWidgetLabel
    } = this.props;
    let translations = messages[selectedLanguageEditor];

    if (!translations) {
      if (selectedLanguageEditor === masterLanguage) {
        /* Generate translations */
        addLanguage(customWidgetId, customWidgetLabel, masterLanguage, true);
      }

      return '';
    }
    /* HACK: For existing module translations with no name we must add them in */


    if (!translations.name) {
      const nameTranslations = {}; // Order matters for the description overlay

      if (selectedLanguageEditor === masterLanguage) {
        nameTranslations.description = '';
      }

      nameTranslations.message = customWidgetLabel;
      translations = translations.set('name', nameTranslations);
    }

    return generateTranslationsFile(translations);
  }

  renderBreadcrumbs() {
    return /*#__PURE__*/_jsx(UIBreadcrumbs, {
      className: "m-top-0",
      flush: true,
      children: /*#__PURE__*/_jsx(UILink, {
        onClick: this.closeEditor,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('editor.backButton')
        })
      })
    });
  }

  renderLabel() {
    return /*#__PURE__*/_jsx(UIFormLabel, {
      children: /*#__PURE__*/_jsx(FormattedReactMessage, {
        message: i18nKey('editor.header'),
        options: {
          language: /*#__PURE__*/_jsx(FormattedMessage, {
            message: languageI18nKey(this.props.selectedLanguageEditor)
          })
        }
      })
    });
  }

  renderRegenerateButton() {
    return /*#__PURE__*/_jsx(UIButton, {
      use: "tertiary-light",
      size: "extra-small",
      onClick: this.regenerateTranslations,
      disabled: this.props.isReadOnly,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('editor.regenerateButton'),
        options: {
          language: I18n.text(languageI18nKey(this.props.selectedLanguageEditor))
        }
      })
    });
  }

  render() {
    const {
      customWidgetId,
      selectedLanguageEditor,
      masterLanguage,
      revisionId,
      partial,
      isReadOnly
    } = this.props;
    const key = `${customWidgetId}${selectedLanguageEditor}`;
    return /*#__PURE__*/_jsxs("div", {
      className: "custom-widget-field-editor-full-screen-editor",
      children: [/*#__PURE__*/_jsx(Toolbar, {
        leftContent: this.renderBreadcrumbs(),
        middleContent: this.renderLabel(),
        rightContent: this.renderRegenerateButton(),
        renderSettings: false
      }), /*#__PURE__*/_jsx(DecoratedCodeMirror, {
        sessionId: key,
        mode: "javascript",
        height: "100%",
        width: "100%",
        autofocus: true,
        value: this.value,
        enableHublValidation: false,
        readOnly: selectedLanguageEditor === masterLanguage || !!revisionId || isReadOnly,
        revisionId: revisionId,
        onChange: this.onChange,
        partial: partial,
        codemirrorType: "json"
      }, key)]
    });
  }

}

LanguageEditor.propTypes = {
  setLanguageEditor: PropTypes.func.isRequired,
  customWidgetId: CustomWidgetPropTypes.customWidgetId.isRequired,
  revisionId: PropTypes.number,
  selectedLanguageEditor: PropTypes.string.isRequired,
  masterLanguage: PropTypes.string,
  messages: PropTypes.object.isRequired,
  updateLanguage: PropTypes.func.isRequired,
  addLanguage: PropTypes.func.isRequired,
  partial: PropTypes.func.isRequired,
  regenerateTranslations: PropTypes.func.isRequired,
  registerStatusBar: PropTypes.func,
  isReadOnly: PropTypes.bool,
  customWidgetLabel: PropTypes.string.isRequired
};
export default compose(connect(makeMapStateToProps, mapDispatchToProps), withPartials())(LanguageEditor);